/**
 * Envelope and error parsing utilities for getProvidersByService (Sourcing API).
 */

/**
 * Represents a single provider tariff item returned from the Sourcing catalog.
 */
export interface ProveedorTarifasItem {
  id?: string | null;
  nombre?: string | null;
  servicioId?: string | null;
  tipoProveedor?: string | null;
  correoReserva?: string | null;
  tarifa?: { id?: string | null } | null;
  oldServiceId?: string;
  [key: string]: unknown;
}

/**
 * Standard envelope returned by the getProvidersByService GraphQL query.
 */
export interface GetProvidersByServiceEnvelope {
  success: boolean;
  code: string;
  message: string;
  data: ProveedorTarifasItem[];
}

/**
 * Known business codes returned by getProvidersByService.
 */
export type GetProvidersByServiceCode =
  | 'PROVIDERS_FOUND'
  | 'NO_SERVICES_FOUND'
  | 'NO_PROVIDERS_LINKED'
  | 'VALIDATION_ERROR'
  | 'SERVICE_SEARCH_FAILED'
  | 'TARIFA_LOOKUP_FAILED'
  | 'PROVIDER_LOOKUP_FAILED'
  | 'INTERNAL_ERROR'
  | string;

/**
 * Client error structure parsed from GraphQL errors or HTTP responses.
 */
export interface GetProvidersByServiceClientError {
  code: GetProvidersByServiceCode;
  message: string;
  validationNombreServicio: boolean;
  retryable: boolean;
}

/**
 * Checks whether the response code indicates that providers were successfully found.
 *
 * @param responseCode - The business status code from the server.
 * @returns Boolean indicating whether providers were found.
 */
export function isProvidersFound(
  responseCode: string | null | undefined,
): boolean {
  return responseCode === 'PROVIDERS_FOUND';
}

/**
 * Checks whether the response code indicates an empty list without technical error.
 *
 * @param responseCode - The business status code from the server.
 * @returns Boolean indicating whether the result is empty but successful.
 */
export function isProvidersEmptySuccess(
  responseCode: string | null | undefined,
): boolean {
  return (
    responseCode === 'NO_SERVICES_FOUND' ||
    responseCode === 'NO_PROVIDERS_LINKED'
  );
}

/**
 * Normalizes a raw GraphQL response envelope into a guaranteed GetProvidersByServiceEnvelope.
 *
 * @param rawEnvelope - Raw response object that may have missing fields.
 * @returns Fully normalized GetProvidersByServiceEnvelope.
 */
export function normalizeProvidersByServiceEnvelope(
  rawEnvelope: Partial<GetProvidersByServiceEnvelope> | null | undefined,
): GetProvidersByServiceEnvelope {
  return {
    success: Boolean(rawEnvelope?.success),
    code: String(rawEnvelope?.code ?? ''),
    message: String(rawEnvelope?.message ?? ''),
    data: Array.isArray(rawEnvelope?.data) ? rawEnvelope!.data! : [],
  };
}

/**
 * Extracts business error code and message from GraphQL errors array.
 *
 * @param graphqlErrors - Array of GraphQL errors returned by the server.
 * @returns Structured client error with retryable flag and validation markers.
 */
export function parseProvidersByServiceGraphqlError(
  graphqlErrors:
    | Array<{
        message?: string;
        extensions?: {
          response?: { code?: string; message?: string };
          code?: string;
        };
      }>
    | null
    | undefined,
): GetProvidersByServiceClientError {
  const firstError = graphqlErrors?.[0];
  const responsePayload = firstError?.extensions?.response;
  const errorCode = String(
    responsePayload?.code ?? firstError?.extensions?.code ?? 'INTERNAL_ERROR',
  );
  const errorMessage = String(
    responsePayload?.message ?? firstError?.message ?? 'Ocurrió un error',
  );
  const isRetryable =
    errorCode === 'SERVICE_SEARCH_FAILED' ||
    errorCode === 'TARIFA_LOOKUP_FAILED' ||
    errorCode === 'PROVIDER_LOOKUP_FAILED' ||
    errorCode === 'INTERNAL_ERROR';

  return {
    code: errorCode,
    message: errorMessage,
    validationNombreServicio: errorCode === 'VALIDATION_ERROR',
    retryable: isRetryable,
  };
}

/**
 * Converts any caught error into a standardized GetProvidersByServiceClientError.
 *
 * @param rawError - Unknown error object caught during execution.
 * @returns Standardized client error.
 */
export function toProvidersByServiceClientError(
  rawError: unknown,
): GetProvidersByServiceClientError {
  if (
    rawError &&
    typeof rawError === 'object' &&
    'message' in rawError &&
    'code' in rawError &&
    'validationNombreServicio' in rawError
  ) {
    return rawError as GetProvidersByServiceClientError;
  }

  const httpErrors = (rawError as any)?.error?.errors;
  if (Array.isArray(httpErrors)) {
    return parseProvidersByServiceGraphqlError(httpErrors);
  }

  const errorMessage =
    rawError instanceof Error
      ? rawError.message
      : typeof rawError === 'string'
        ? rawError
        : 'Ocurrió un error inesperado al consultar proveedores';

  return {
    code: 'INTERNAL_ERROR',
    message: errorMessage,
    validationNombreServicio: false,
    retryable: true,
  };
}
